import React, { createContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { UserInfoResponse } from "@storyboard/web-client/auth";
import { user, workspaceId } from "@/store/scope";

type AuthState = {
  user?: UserInfoResponse | null;
  loggedIn?: boolean;
  workspaceId?: string | null;
  accountId?: string | null;
};
export const AuthContext = createContext<AuthState>({
  user: null,
  loggedIn: false,
  workspaceId: null,
  accountId: null,
});

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const urlParams = useParams();
  const navigate = useNavigate();
  const [activeUser, setActiveUser] = useState<UserInfoResponse | null>(null);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);

  // do not redirect to a workspace if on any of the following pages:
  const allowedPages = ["/onboarding", "/notification-redirect", "/maintenance-mode"];
  const currentPath = window.location.pathname;
  const allowedPageWithoutRedirect = !allowedPages.some((path) => currentPath.includes(path));

  const setAuthState = (response: UserInfoResponse | null) => {
    if (urlParams?.workspaceId && response?.workspaces) {
      setActiveUser(() => response);
      setLoggedIn(() => true);
    } else {
      if (response?.workspaces?.length > 0) {
        setActiveUser(() => response);
        setLoggedIn(() => true);
        if (allowedPageWithoutRedirect) {
          const redirect = `/workspaces/${workspaceId}`;
          navigate(redirect, { replace: true });
        }
      } else if (response?.workspaces?.length === 0) {
        setActiveUser(() => response);
        setLoggedIn(() => true);
      } else {
        setActiveUser(() => null);
        setLoggedIn(() => false);
      }
    }
  };

  useEffect(() => {
    if (user) {
      setAuthState(user);
    }
  }, [user]);

  const authState = {
    user: activeUser,
    loggedIn,
    workspaceId,
    accountId: activeUser?.accounts[0]?.id,
  };

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
